import * as THREE from 'three';
import {
    FloorsObjects,
    isMat,
    Lights,
    MaterialManager,
    PSSceneManagement,
    SetbackObjects,
    SizesObjects,
    ThreeView,
    TimeLapseTween, View3dGlobal,
    WarpPoint,
    WarpPoints,
    webcadcamera,
    webcadDefines
} from '.';
import * as marietta from '../..';
import * as network from '../../pixelstreaming/network';
import { BUILDING_TYPE_MANSION, ITEM_MODE_NO } from '../../webcad/Constants';
import { ApplyRecognize } from '../../webcad/util/Recognize/ApplyRecognize';
import { WizardTool } from '../../webcad/view/dialog/WizardLiDAR';
import {
    Camera, CanvasInfo,
    defines,
    events, geoUtils,
    ItemManager,
    matUtils,
    Scene,
} from '../base';
import { optChangeMode, optChangeWizardMode, Option3D, optRecognizeMode } from './util/Option3DInterface';
import { Mark } from '../../webcad/item';

interface visibleItemType {
    extrasType: string,
    subtypes: string[]
}

interface visibleDialogType {
    dialogType: string,
    extrasTypes: visibleItemType[]
}

/* WebCadItemManager
 * 設備家具情報の処理.
 */
export default class WebCadItemManager extends ItemManager {
    private _houseObjects: THREE.Group;

    private _floorsObjects: FloorsObjects; 	// 非表示にするとき用.

    private _sizesObjects: SizesObjects; // 寸法表示用.

    private _setbackObjects: SetbackObjects; // 斜線規制

    private _fittings: THREE.Group[];		// 建具

    private _furnitureObjects: THREE.Group[];	// 家具・設備・外構

    private _houseData: THREE.Object3D[];

    private _animObjects: THREE.Group[]; // 建具アニメーション対象

    private _lights: Lights; // 光源アクセス用

    private _lightTargets: THREE.Group; // スポットライトターゲット用

    private _OtherShapes: THREE.Group;

    private _LiDARShapes: THREE.Group;

    private _AddItemShapes: THREE.Group; // 3D上で追加した形状

    private _binaryData: ArrayBuffer; // arrayBuffer

    private _gltfExtras: any; // glbのuserData
    private _outerLine: THREE.Vector2[];

    private _hour: number; // 時間(temporary)

    private _billBoards: THREE.Mesh[];

    private _hilites: THREE.Group;

    private _hiliteMaterials: THREE.Group;

    private _warps: WarpPoints;

    private _floorHeights: number[];

    private _planHeight: number;

    private _xSize: number;

    private _ySize: number;

    private _topViewSize: THREE.Vector2;

    private _topViewRate: number;

    private _textureLoadingCount: number;

    private _mapItems: any = {};

    public get houseObjects(): THREE.Group { return this._houseObjects; }

    public get floorsObjects(): FloorsObjects { return this._floorsObjects; }
    
    public get houseData(): THREE.Object3D[] {
        return this._houseData;
    }

    public get xSize(): number { return this._xSize; }
    public set xSize(value) { this._xSize = value; }

    public get ySize(): number { return this._xSize; }
    public set ySize(value) { this._ySize = value; }

    public get sizesObjects(): SizesObjects { return this._sizesObjects; }

    public get setbackObjects(): SetbackObjects { return this._setbackObjects; }

    public get fittings(): THREE.Group[] { return this._fittings; }

    public get furnitureObjects(): THREE.Group[] { return this._furnitureObjects; }

    public get animObjects(): THREE.Group[] { return this._animObjects; }

    public get lights(): Lights { return this._lights; }

    public get lightTargets(): THREE.Group { return this._lightTargets; }

    private get billBoards(): THREE.Mesh[] { return this._billBoards; }

    public get OtherShapes(): THREE.Group { return this._OtherShapes; }

    public get LiDARShapes(): THREE.Group { return this._LiDARShapes; }

    public get AddItemShapes(): THREE.Group { return this._AddItemShapes; }

    public get BinaryData(): ArrayBuffer { return this._binaryData; }
    public set BinaryData(value: ArrayBuffer) { this._binaryData = value; }

    public get GltfExtras(): any { return this._gltfExtras; }
    public set GltfExtras(value: any) { this._gltfExtras = value; }

    public get outerLine(): THREE.Vector2[] {
        return this._outerLine;
    }
    public set outerLine(value: THREE.Vector2[]) {
        this._outerLine = value;
    }
    
    public get warps(): WarpPoints { return this._warps; }

    public get floorHeights(): number[] { return this._floorHeights; }

    public get planHeight(): number { return this._planHeight; }
    public set planHeight(value: number) { this._planHeight = value; }

    public set opeMode(mode: defines.OpeMode) { /* do nothing */ }

    public get textureLoadingCount(): number { return this._textureLoadingCount; }

    /*
     * コンストラクタ.
     */
    constructor(scene: Scene, eventDispatcher?: events.EventDispatcher) {
        super(scene, eventDispatcher);

        this._floorsObjects = new FloorsObjects();
        this._sizesObjects = new SizesObjects();
        this._setbackObjects = new SetbackObjects();
        this._warps = new WarpPoints();
        // this.pickermanager = new PickerManager();
        this._topViewSize = new THREE.Vector2(0, 0);
        this._topViewRate = 0;
        this.lights.addLights(scene);
        this._hour = 12;
    }

    /* reset
     * データのリセット.
     */
    public reset(options: defines.Options) {
        super.reset(options);

        if (this._houseObjects != undefined) {
            this.scene.me.remove(this._houseObjects);
            geoUtils.disposeObject(this._houseObjects);
            this._houseObjects = new THREE.Group();
        }
        if (this._floorsObjects != undefined) {
            this._floorsObjects.reset();
        }
        if (this._sizesObjects != undefined) {
            this._sizesObjects.reset(this.scene);
        }
        if (this._warps != undefined) {
            this._warps.reset();
        }
        if (this._setbackObjects != undefined) {
            this._setbackObjects.reset(this.scene);
        }

        this._floorHeights = [];
        this._planHeight = 0;
        this._billBoards = [];

        const disposeObjects = (objects: THREE.Group[]) => {
            if (objects == undefined) return;
            for (let i = 0; i < objects.length; i++) {
                if (this.scene != null) this.scene.remove(objects[i]);
                geoUtils.disposeObject(objects[i]);
                objects[i] = null;
            }
        }
        disposeObjects(this._furnitureObjects);
        this._furnitureObjects = [];
        disposeObjects(this._animObjects);
        this._animObjects = [];
        disposeObjects(this._fittings);
        this._fittings = [];
        this._houseData = [];

        const clearShapes = (obj) => {
            while (obj.children.length > 0) {
                clearShapes(obj.children[0])
                obj.remove(obj.children[0]);
            }
            if (obj.geometry) obj.geometry.dispose()

            if (obj.material) {
                //in case of map, bumpMap, normalMap, envMap ...
                Object.keys(obj.material).forEach(prop => {
                    if (!obj.material[prop])
                        return
                    if (typeof obj.material[prop].dispose === 'function')
                        obj.material[prop].dispose()
                })
                obj.material.dispose()
            }
        }

        // 寸法線の削除
        const sizeObjects = this.scene.me.getObjectByName('寸法線');
        if (sizeObjects) {
            clearShapes(sizeObjects);
            this.scene.me.remove(sizeObjects);
        }

        if (this._OtherShapes) {
            clearShapes(this._OtherShapes);
            this.scene.me.remove(this._OtherShapes);
            this._OtherShapes = null;
        }

        if (this._AddItemShapes) {
            clearShapes(this._AddItemShapes);
            this.scene.me.remove(this._AddItemShapes);
            this._AddItemShapes = null;
        }

        if (this._lights == undefined) this._lights = new Lights();
        const reset_env: boolean = options.is(defines.ResetOptions.resetEnv, true);
        this._lights.reset(reset_env ? undefined : /ambient|sun/);

        if (this._lightTargets != undefined) {
            if (this.scene != null) this.scene.remove(this._lightTargets);
            geoUtils.disposeObject(this._lightTargets);
        }
        this._lightTargets = new THREE.Group();

        this._textureLoadingCount = 0;
        this.resetMapItems();
    }

    /* setForTime
     */
    public setForTime(hour: number, matmgr: MaterialManager, immediate?: boolean) { // temporary for testing...
        this._hour = hour;
        if (!matmgr.materialInited) return;

        const to = this.lights.getGoal(matmgr, (hour > 18) || (hour < 6));
        if ((immediate != undefined) && immediate) {
            this._lights.apply(to);
        } else {
            const from = this.lights.getCurrent();
            const tween = new TimeLapseTween(this.lights, from, to);
            tween.prepare().start();
        }
    }

    public getTime(): number {
        return this._hour;
    }

    public resetMapItems() {
        if (this._mapItems != undefined) {
            for (const key in this._mapItems) {
                geoUtils.disposeObject(this._mapItems[key]);
            }
        }
        this._mapItems = {};
    }

    /* changeHouseData
     * オブジェクトを変更する
     */
    public changeHouseData(oldObj: THREE.Object3D ,object: THREE.Object3D) {
        const index = this._houseData.findIndex((data: THREE.Object3D) => {
            let bool = false;
            data.traverse((obj) => {
                if (obj.uuid == oldObj.uuid) bool = true;
            });
            if (bool) return true;
        });
        if (index == -1) console.assert(!(index == -1), 'no item change');
        this._houseData.splice(index, 1, <THREE.Object3D>object);
    }

    /* addHouseData
     * 新しいオブジェクトを追加する
     */
    public addHouseData(object: THREE.Object3D): void {
        this._houseData.push(<THREE.Object3D>object);
    }

    /* setHouseObjects
     * 可能な限りオブジェクトをマージしながら、セットする.
     */
    public setHouseObjects(houseObjects: THREE.Group): void {
        this._houseObjects = houseObjects;
        houseObjects.traverse((object: THREE.Object3D) => {
            const mesh = <THREE.Mesh>object;
            if (!mesh.isMesh) return;
            this._houseData.push(<THREE.Object3D>mesh);
        });
    }

    /* setHouseObject
     * 可能な限りオブジェクトをマージしながら、セットする.
     */
    public setHouseObject(obj3d: THREE.Object3D, layer: string, objects: { [key: string]: THREE.Object3D }): void {
        // @ts-ignore
        if (obj3d.isMesh) {
            const mesh: THREE.Mesh = <THREE.Mesh>(obj3d);
            const materialNames = matUtils.materialNames(<any>mesh.material);
            const name = `${layer}:${materialNames}`;
            const mobj: THREE.Object3D = objects[name];
            if (mobj == undefined) {
                objects[name] = mesh.parent;
            } else {
                const mmesh: THREE.Mesh = <THREE.Mesh>(mobj.children[0]);
                const mat = new THREE.Matrix4();
                mat.getInverse(mmesh.parent.matrix);
                mat.multiply(mesh.parent.matrix);
                mesh.geometry.applyMatrix4(mat);
                // geoUtils.mergeMeshes(mmesh, mesh);
            }
        } else if (obj3d.children != undefined) {
            for (let i = 0; i < obj3d.children.length; i++) {
                this.setHouseObject(obj3d.children[i], layer, objects);
            }
        }
    }

    /* registObject
     * 家本体のデータを分類.
     */
    public registHouse(): void {
        if (this.houseObjects == undefined) return;

        this.houseObjects.traverse((object) => this.registFloorObject(object));
        this.registObject_(this.houseObjects);
    }

    /* registObjects
     * THREE.Object3Dを分類
     */
    public registObjects(obj3d: THREE.Object3D): void {
        this.registFloorObject(obj3d);
        this.registObject_(obj3d);
    }

    /* sortHouseMesh
     * Meshデータを分類.
     */
    private registObject_(obj3d: THREE.Object3D): void {
        obj3d.traverse((obj) => {
            (<THREE.Mesh>obj).isMesh && this.registHitMesh((<THREE.Mesh>obj));
        });
    }

    /* registHitMesh
     * ポリゴンメッシュの登録（当たり判定、階層）.
     */
    private registHitMesh(mesh: THREE.Mesh): void {
        if (mesh.name == 'out00-limit-node') return;     // 斜線オブジェクトは対象外とする

        this.meshs.push(mesh);

        const HitObject = (materialName: string) => {
            return isMat.innerWallCeiling(materialName)
                || isMat.outerWall(materialName)
                || isMat.outerWallKosi(materialName)
                || isMat.base(materialName)
                || isMat.balcony(materialName);
        };

        const materialNames = matUtils.materialNames(<any>mesh.material);
        for (let i = 0; i < materialNames.length; i++) {
            const materialName = materialNames[i];

            if (materialName != '') {
                if (HitObject(materialName)) {
                    this.hitMeshs.push(mesh);
                    break;
                } else if (isMat.mune(materialName)) {
                    // CHANGED: 棟の layer の値が L2 になっているためメッシュを _roofObjects に加える（暫定）
                    this._floorsObjects.add(mesh, 'R2');
                    break;
                }
            }
        }
    }

    public updateHouseMesures(): void {
        const ua = marietta.webcad.WebCADGlobal.userAgentManager;
        switch (true) {
            case ua.isPC:
                if ($('#topview_1').length > 0) {
                    this._topViewSize.x = $('#topview_1').prop('width') - webcadDefines.Limits.topViewMargin * 2;
                    this._topViewSize.y = $('#topview_1').prop('height') - webcadDefines.Limits.topViewMargin * 2;
                } else {
                    this._topViewSize.x = 0;
                    this._topViewSize.y = 0;
                }
                break;
            case ua.isTablet:
                this._topViewSize.x = 640 - 40 * 2;
                this._topViewSize.y = 480 - 40 * 2;
                break;
            case ua.isMobile:
                this._topViewSize.x = 320 - 20 * 2;
                this._topViewSize.y = 240 - 20 * 2;
                break;
            default: break;
        }

        if ((this._xSize > 0) && (this._ySize > 0) && (this._topViewSize.x > 0) && (this._topViewSize.y > 0)) {
            this._topViewRate = Math.min(this._topViewSize.x / this._xSize, this._topViewSize.y / this._ySize);
        } else {
            this._topViewRate = 0.0;
        }
    }

    /* pt3DToPtMini
     * 座標変換:3D->MiniMap.
     */
    public pt3DToPtMini(pt3: THREE.Vector3): THREE.Vector2 {
        if (this._topViewRate <= 0) this.updateHouseMesures();
        if (this._topViewRate > 0) {
            return new THREE.Vector2(
                this._topViewSize.x / 2.0 + pt3.x * this._topViewRate,
                this._topViewSize.y / 2.0 + pt3.z * this._topViewRate,
            );
        }
        return undefined;
    }

    /* ptMiniToPt3D
     * 座標変換:MiniMap->3D.
     */
    private ptMiniToPt3D(pt2: THREE.Vector2): THREE.Vector3 {
        if (this._topViewRate <= 0) this.updateHouseMesures();
        if (this._topViewRate > 0) {
            return new THREE.Vector3(
                (pt2.x - this._topViewSize.x / 2.0) / this._topViewRate,
                0,
                (pt2.y - this._topViewSize.y / 2.0) / this._topViewRate,
            );
        }
        return undefined;
    }

    /* dir3DToDirMini
     * 方向変換:3D->MiniMap.
     */
    public dir3DToDirMini(pt3: THREE.Vector3): number {
        if (this._topViewRate <= 0) this.updateHouseMesures();
        if (this._topViewRate > 0) {
            return Math.atan2(-pt3.x, -pt3.z);
        }
        return undefined;
    }

    public isWall(obj: any): any {
        if (obj == undefined) return false;
        if (obj.name.match(/(wall)|(kosi)/)) return true;
        return this.isWall(obj.parent);
    }

    public toWallDist(objs_y: any[]): number {
        for (let i = 0; i < objs_y.length; i++) {
            if (this.isWall(objs_y[i].object)) { return objs_y[i].distance; }
        }
        return -1;
    }

    public findPath(camPos: THREE.Vector3, lookAt: THREE.Vector3): THREE.Vector3 {
        let newlookAt: THREE.Vector3 = new THREE.Vector3();
        const step: number = Math.PI / 4.0;
        let distMax: number = 0;
        for (let i = 1; i <= 8; i++) {
            const test: THREE.Vector3 = new THREE.Vector3(0, 0, 1).applyAxisAngle(webcadDefines.WebCadCameraDefs.turnAxis, step * i).normalize();
            {
                const ray_y = new THREE.Raycaster(camPos, test);
                const objs_y = ray_y.intersectObjects(this.meshs, true);
                const dist: number = this.toWallDist(objs_y);
                if (dist > distMax) {
                    distMax = dist;
                    newlookAt = test;
                }
            }
        }
        if (distMax > webcadDefines.WebCadCameraDefs.wlkthOffWall) return newlookAt;
        return lookAt;
    }

    public setCameraPosition(pos: THREE.Vector3, dir: THREE.Vector3): void {
        if (!pos) return;
        const cameras: webcadcamera.WebCadCameras = ThreeView.get().wcCameras;
        const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
        cam.position.set(0, 0, 0);
        cam.lookAt = dir.clone().normalize();
        const hgt: number = cameras.getDownHeight(pos.x, this.floorToHeight(cameras.floor) + 0.05, pos.z); // 0.05 for margin.
        cam.position.set(pos.x, hgt, pos.z);
        cam.notify(defines.EventNames.cameraReset, {});
        cameras.notifyChange(defines.CameraDefs.wlkthCamNo);
        new PSSceneManagement().sendCamera();
    }

    // minimapによるカメラ移動
    public setCameraFromMiniPt(v2: THREE.Vector2): void {
        const pt3: THREE.Vector3 = this.ptMiniToPt3D(v2);
        if (pt3 == undefined) return;
        const cameras: webcadcamera.WebCadCameras = ThreeView.get().wcCameras;
        const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
        const hgt: number = cameras.getDownHeight(pt3.x, this.floorToHeight(cameras.floor) + 0.05, pt3.z); // 0.05 for margin.
        cam.position.set(pt3.x, hgt, pt3.z);
        this.checkFacingWall(cameras, cam, true);
        cam.notify(defines.EventNames.wtHitCheck, {});
        cam.notify(defines.EventNames.cameraReset, {});
        new PSSceneManagement().sendCamera();
    }

    public checkFacingWall(cameras: webcadcamera.WebCadCameras, cam: Camera, skyGaze?: boolean): void {
        // カメラの向きが壁の前なら、調整する.
        if (skyGaze == undefined) skyGaze = false;
        const curLookAt: THREE.Vector3 = cam.lookAt.clone();
        curLookAt.y = 0;
        const pos: THREE.Vector3 = new THREE.Vector3();
        pos.x = cam.position.x - (curLookAt.x * 0.05);
        pos.y = cam.position.y - (curLookAt.y * 0.05);
        pos.z = cam.position.z - (curLookAt.z * 0.05);
        const ray_y: THREE.Raycaster = new THREE.Raycaster(pos, curLookAt); // step a little bit back..
        const objs_y = ray_y.intersectObjects(this.meshs, true);
        if (objs_y.length > 0) {
            for (let i = 0; i < objs_y.length; i++) { // workaround
                if (objs_y[i].object instanceof THREE.LineSegments) continue;
                else if (objs_y[i].distance >= webcadDefines.WebCadCameraDefs.wlkthOffWall) break;
                else if (this.isWall(objs_y[i].object)) {
                    const newLookAt: THREE.Vector3 = this.findPath(cam.position, curLookAt);
                    cameras.cameraRotate(cam, newLookAt);
                    break;
                }
            }
        } else if (skyGaze) { // looking vacant space..
            const newLookAt: THREE.Vector3 = new THREE.Vector3(0 - pos.x, webcadDefines.WebCadCameraDefs.wlkthHeight - pos.y, 0 - pos.z);
            cameras.cameraRotate(cam, newLookAt);
        }
    }

    /* showFloor
     * 階の表示非表示の制御.
     */
    public showFloor(floor: number, visible: boolean): void {
        this.wireframe = false;
        this.floorsObjects.floor = floor;
        this.sizesObjects.floor = (floor >= webcadDefines.Limits.roofNo ? 0 : floor);
        View3dGlobal.shared.setItemDisplay(floor);
    }

    /* getFloorShown
     * 階の表示非表示の状態.
     */
    public getFloorShown(): number {
        return this.floorsObjects.floor;
    }

    /* updateFloorVis
     * 現在の設定に合わせて各階表示を復元する.
     */
    public updateFloorVis() {
        this.floorsObjects.floor = this.floorsObjects.floor;
    }

    /* getFloor
     * layerから階数を返す.
     */
    public static getFloor(obj: THREE.Object3D): number {
        if (obj.userData.layer == undefined) return 1;
        const strs = obj.userData.layer.split(/L|R/);
        if (strs.length < 2) return 1;
        return Number(strs[1]);
    }

    //ワールドポジションから階高を推定する
    public static getFloorFromPoint(point: THREE.Vector3) {
        const height = point.y;
        
        if (ThreeView.get().wcCameras.isOrbitCameraMode()) return 1;
        if(height < ThreeView.get().wcItemmgr.floorHeights[1]) return 1;
        if(ThreeView.get().wcItemmgr.floorHeights[1] < height && height < ThreeView.get().wcItemmgr.floorHeights[2]) return 2;
        if(ThreeView.get().wcItemmgr.floorHeights[2] < height) return 3;
    }

    /* registFloorObject
     * オブジェクトを階層登録
     */
    public registFloorObject(obj3d: THREE.Object3D): void {
        const isCeiling = (obj: THREE.Object3D) => obj.name.match(/^(?=.*rom)(?=.*ceiling)/);
        const isLightModel = (obj: THREE.Object3D) => obj.userData.attribute != null && ((obj.userData.attribute.objectId != null) && (obj.userData.attribute.objectId.match(/^lig/)));

        if (isCeiling(obj3d) || isLightModel(obj3d)) {
            const floorNo: number = WebCadItemManager.getFloor(obj3d);
            this._floorsObjects.add(obj3d, floorNo == webcadDefines.Limits.maxFloor ? `R${floorNo}` : `L${floorNo + 1}`);
        } else {
            if (obj3d.userData.layer) {
                this._floorsObjects.add(obj3d, obj3d.userData.layer);
            }
        }
    }

    // 斜線アイテムの登録
    // 斜線アイテムはWalkthroughで衝突させないため,meshesに入れない.
    public registSetBack(objects: THREE.Group): void {

        const setBack = <THREE.Mesh>(objects.getObjectByName('out00-limit-node'));
        if (setBack == null) return;

        console.assert(setBack.isMesh, '斜線 is not mesh !!!!!');

        const materialNames = matUtils.materialNames(<any>setBack.material);

        const result = materialNames.some((materialName: string) => isMat.setback(materialName));
        if (!result) return;

        if (this.houseObjects) this.houseObjects.remove(setBack); // 一旦外す.

        const wireframe = new THREE.WireframeGeometry(setBack.geometry);
        const material =  new THREE.LineBasicMaterial({ color: 0xaaaa00, opacity: 0.2, transparent: true });
        const line = new THREE.LineSegments(wireframe, material);

        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        setBack.matrixWorld.decompose(position, quaternion, scale);
        line.position.add(position);
        line.quaternion.copy(quaternion);
        line.scale.copy(scale);

        const group = new THREE.Group();
        group.add(line);
        group.add(setBack);
        this.setbackObjects.add(group);
    }

    /* processWarpPoints
     * ワープポイント情報の処理.
     */
    public processWarpPoints(commentsObj: HTMLElement) {
        const count: number = WarpPoint.extractWarps(commentsObj, this._warps);
    }

    /* findObjects
     * アイテムを名前で検索する.
     */
    public findObjects(name: any, floor: number): THREE.Object3D[] {
        const founds: THREE.Object3D[] = [];
        const floorName: string = `L${String(floor)}`;
        if (this.floorsObjects.hasLabel(floorName)) {
            const objects = this.floorsObjects.getObjects(floorName);
            for (let i = 0; i < objects.length; i++) {
                if (objects[i].name.match(name)) {
                    founds.push(objects[i]);
                }
            }
        }
        return founds;
    }

    /* replaceObject
     * アイテムを入れ替える.
     */

    public replaceObject(target: THREE.Object3D, replace: THREE.Object3D) {
        // replace in houseObjects
        try {
            this.houseObjects.traverse((child) => {
                if (child == target) {
                    child.parent.add(replace);
                    child.parent.remove(child);
                    geoUtils.disposeObject(target);
                    throw 'done!'; // mission complete, escape immediately.
                }
            });
        } catch (Error) {
            // do nothig // console.log(Error);
        }
    }

    /* startLoadingTexture
     */
    public startLoadingTexture(url: string) {
        this._textureLoadingCount++;
    }

    /* endLoadingTexture
     */
    public endLoadingTexture(url: string, option3D: Option3D = null): void {
        this._textureLoadingCount--;

        if (this._textureLoadingCount <= 0) {
            if (View3dGlobal.shared.canRestoreCameraState() && !ThreeView.get().wcItemmgr.messageShown) {
                ThreeView.get().wcCameras.skipPhase();
                ThreeView.get().cameras.no = defines.CameraDefs.wlkthCamNo;
                View3dGlobal.shared.restoreCameraState();
            } else if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType === BUILDING_TYPE_MANSION) {
                // TODO: brush-up
                ThreeView.get().wcCameras.skipPhase();
                ThreeView.get().cameras.no = defines.CameraDefs.wlkthCamNo;
                let cameraState = View3dGlobal.shared.cameraState;
                let entPos = ThreeView.get().cameras.camera.getSetting(webcadDefines.WarpPointNames.entrance + webcadDefines.WarpPointNames.position);
                if (entPos == undefined) entPos = webcadDefines.WebCadCameraDefs.initWlkThPos;
                cameraState.position = new THREE.Vector3(entPos.x, entPos.y,entPos.z);
                View3dGlobal.shared.cameraState = cameraState;
                const cameras: webcadcamera.WebCadCameras = ThreeView.get().wcCameras;
                const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
                this.checkFacingWall(cameras, cam);
            } else {
                ThreeView.get().cameras.no = defines.CameraDefs.orbitCamNo;
            }
            ThreeView.get().wcItemmgr.resetMapItems(); // TODO.
            this.eventDispatcher.dispatchEvent(webcadDefines.WebCadEventNames.loadingTexFinished);

            // ビジーフィルタを削除
            if (marietta.webcad.WebCADGlobal != undefined) marietta.webcad.WebCADGlobal.hideBusy();
            // pixelstreamingに接続済みならシーンを送信
            if (ThreeView.get().pscommunication.status == network.statustype.connect) {
                new PSSceneManagement().createScene();
            }

            // ピン紐づけアイテム変更
            if ((<optChangeMode>option3D)?.isChangeMode) {
                ThreeView.get().wcCameras.skipPhase();
                if ((<optChangeWizardMode>option3D)?.isWizardMode) {
                    new WizardTool().wizardChangeMode((<optChangeMode>option3D)?.iteminfo);
                } else {
                    new WizardTool().changeMode((<optChangeMode>option3D)?.iteminfo);
                }
            }

            // 画像検知
            if ((<optRecognizeMode>option3D)?.isRecogninzeMode) {
                new ApplyRecognize().apply((<optRecognizeMode>option3D).markList);
                if ((<optRecognizeMode>option3D)?.showWizardDialog) ThreeView.get().selectWizard();
            }
        } else {
            // 失敗したときの処理

            // FCA_KENGINE_WEBCAD-228 ネットワーク環境が悪い場所での3D起動時にはなんらかの配慮を入れるにて追加した処理が不要で、
            // これまでエラーで実質呼べていなかったのを修正したところ不具合が発覚。もともと必要ない処理なので、コメントアウトします。 : 2020-03-04

            // this.eventDispatcher.dispatchEvent(webcadDefines.WebCadEventNames.setup3DFailed);

        }
    }

    /* errorLoadingTexture
     */
    public errorLoadingTexture(message: string): void {
        this.endLoadingTexture(message);
    }

    public addOtherShapes(shape: THREE.Object3D) {
        if (!this._OtherShapes) {
            const group = new THREE.Group();

            group.name = 'Other Shape';
            group.userData.type = 'other';
            this._OtherShapes = group;
        }
        this._OtherShapes.add(shape);
    }

    public addLiDARShapes(shape: THREE.Object3D) {
        if (!this.LiDARShapes) {
            const group = new THREE.Group();

            group.name = 'Other Shape';
            group.userData.type = 'other';
            this._LiDARShapes = group;
        }
        this._LiDARShapes.add(shape);
    }

    // 3D上で形状を追加
    public addItemShapes(shape: THREE.Object3D) {
        if (!this._AddItemShapes) {
            const group = new THREE.Group();

            group.name = 'AddItem Shape';
            group.userData.type = 'addItem';
            this._AddItemShapes = group;
        }
        this._AddItemShapes.add(shape);
    }

    public removeOtherShapes(shape: THREE.Object3D) {
        if (this._OtherShapes == null) return;
        this._OtherShapes.remove(shape);
        geoUtils.disposeObject(shape);
    }

    // 3D上で形状を削除
    public removeAddItemShapes(shape: THREE.Object3D) {
        if (this._AddItemShapes == null) return;
        this._AddItemShapes.remove(shape);
        geoUtils.disposeObject(shape);
    }

    // 3D上で追加した形状かどうか
    public isAddItem(object: THREE.Object3D): boolean {
        let isBool = false;
        this._AddItemShapes.traverse((obj: THREE.Object3D) => {
            if (obj.uuid == object.uuid) isBool = true;
        });
        return isBool;
    }

    // binaryデータとextrasの初期化
    public resetData() {
        if (this._binaryData) {
            this._binaryData = null;
        }

        if (this._gltfExtras) {
            this._gltfExtras = null;
        }
    }

    /* setupBillBoards
     * billboard のセットアップ.
     */
    public setupBillBoards(): void {
        for (let i = 0; i < this._furnitureObjects.length; i++) {
            const scope = this._billBoards;
            this._furnitureObjects[i].traverse((child) => {
                const mesh = (<THREE.Mesh>child);
                if (mesh.isMesh && child.parent.name.match(webcadDefines.MatchDefs.billBoardNames)) {
                    scope.push(mesh);
                }
            });
        }
    }

    /* updateBillBoard
     * billboard の更新.
     */
    public updateBillBoard(mesh: THREE.Mesh, camera: Camera): void {
        mesh.geometry.computeBoundingSphere();

        const { center } = mesh.geometry.boundingSphere;
        const camPosition: THREE.Vector3 = camera.position.clone();
        mesh.worldToLocal(camPosition);
        const lookDir: THREE.Vector3 = new THREE.Vector3(camPosition.x - center.x, 0, camPosition.z - center.z).normalize();
        const norm: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
        const bgeometry: THREE.BufferGeometry = <THREE.BufferGeometry>(mesh.geometry);
        const normals: THREE.BufferAttribute = <THREE.BufferAttribute>((bgeometry as any).attributes.normal);
        if (normals.count > 0) {
            for (let j = 0; j < normals.count; j++) {
                norm.x += normals.array[3 * j];
                norm.z += normals.array[3 * j + 2];
            }
            norm.x /= normals.count;
            norm.z /= normals.count;
            norm.normalize();
            if (isNaN(norm.x)) norm.x = 0;
            if (isNaN(norm.y)) norm.y = 0;
            if (isNaN(norm.z)) norm.z = 0;

            const rad = Math.atan2(lookDir.x, lookDir.z) - Math.atan2(norm.x, norm.z);
            mesh.rotateOnAxis(new THREE.Vector3(0, 1, 0), rad);
            mesh.updateMatrixWorld(true);
        }
    }

    /* updateBillBoards
     * billboards の更新.
     */
    public updateBillBoards(camera: Camera): void {
        for (let i = 0; i < this._billBoards.length; i++) {
            this.updateBillBoard(this._billBoards[i], camera);
        }
    }

    /* updateSizeLabels
     * sizeLabels の更新.
     */
    public updateSizeLabels(camera: Camera): void {
        if (this.sizesObjects.visible) {
            const vc: THREE.Vector3 = new THREE.Vector3();
            this.sizesObjects.update(camera.me.getWorldDirection(vc));
        }
    }

    //
    public removefurnitureObject(object : THREE.Object3D) {
        if (object == null) return;
        const index = this._furnitureObjects.findIndex((obj) => obj.uuid == object.uuid);

        if (index == -1) return

        const obj3d = this._furnitureObjects[index];
        if (obj3d){
            obj3d.parent.remove(obj3d);
            geoUtils.disposeObject(obj3d);
            this._furnitureObjects.splice(index,1);
        }
    }

    /* heightToFloor
     * 高さから階を推測する.
     */
    public heightToFloor(height: number): number {
        for (let i = 0; i < this._floorHeights.length; i++) {
            if (this._floorHeights[i] + webcadDefines.Limits.floorHeightMargin > height) { return i + 1; }
        }
        return 1;
    }

    /* floorToHeight
     * 階から高さを返す.
     */
    public floorToHeight(floor: number): number {
        if ((floor < webcadDefines.Limits.minFloor) || (floor > webcadDefines.Limits.maxFloor)) return 0;
        return ThreeView.get().wcItemmgr.floorHeights[floor - 1];
    }

    /* getItemVisible
     * item の表示/非表示.
     */
    public getItemVisible(itemType: webcadDefines.PartType): boolean {
        for (let i = 0; i < this.furnitureObjects.length; i++) {
            const obj = this.furnitureObjects[i];
            if (obj.type == itemType) return obj.visible;
        }
        return false;
    }

    /* setItemVisible
     * item の表示/非表示.
     */
    public setItemVisible(itemType: webcadDefines.PartType, value: boolean, floor?: number) {
        for (let i = 0; i < this.furnitureObjects.length; i++) {
            const obj = this.furnitureObjects[i];
            if (floor && floor < webcadDefines.Limits.roofNo) {
                if (obj.type == itemType && WebCadItemManager.getFloor(obj) <= floor) obj.visible = value;
            } else if (obj.type == itemType) obj.visible = value;
        }
    }

    /**
     * フロアでフィルタリングした後、すべてのオブジェクトを単一の配列で取得
     */
    public getAllFloorObjects(): THREE.Object3D[] {
        const floorObjects = [];
        if (this.floorsObjects.floor >= webcadDefines.Limits.roofNo) {
            for (let i = webcadDefines.Limits.maxFloor; i >= 1; i--) {
                const rObj = this.floorsObjects.getObjects(`R${i}`);
                const lObj = this.floorsObjects.getObjects(`L${i}`);
                if (rObj) floorObjects.push(rObj);
                if (lObj) floorObjects.push(lObj);
            }
        } else {
            for (let i = webcadDefines.Limits.maxFloor; i >= 1; i--) {
                if (i <= this.floorsObjects.floor) {
                    const rObj = this.floorsObjects.getObjects(`R${i}`);
                    const lObj = this.floorsObjects.getObjects(`L${i}`);
                    if (rObj) floorObjects.push(rObj);
                    if (lObj) floorObjects.push(lObj);
                }
            }
        }
        const objects: THREE.Object3D[] = [];
        for (let i = 0; i < floorObjects.length; i++) {
            for (let j = 0; j < floorObjects[i].length; j++) {
                objects.push(floorObjects[i][j]);
            }
        }
        return objects;
    }

    private typeTable(): visibleDialogType[] {
        const result: visibleDialogType[] = [
            // - 屋根 --
            {
                dialogType: webcadDefines.PartType.roof,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.ROOF,
                        subtypes: [
                            webcadDefines.ExtrasSubType.ROOF,
                            webcadDefines.ExtrasSubType.MUNE,
                            webcadDefines.ExtrasSubType.HANA,
                            webcadDefines.ExtrasSubType.URA,
                            webcadDefines.ExtrasSubType.WATERPROOF
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.DEC,
                        subtypes: [
                            webcadDefines.ExtrasSubType.BALRAIL,
                            webcadDefines.ExtrasSubType.KAZARI,
                            webcadDefines.ExtrasSubType.YOROI,
                            webcadDefines.ExtrasSubType.FBOX,
                            webcadDefines.ExtrasSubType.LATTICE,
                            webcadDefines.ExtrasSubType.CORNER,
                            webcadDefines.ExtrasSubType.HISASI
                        ]
                    },
                ]
            },
            // - 天井 --
            {
                dialogType: webcadDefines.PartType.ceiling,
                extrasTypes:[
                    {
                        extrasType: webcadDefines.ExtrasType.CEILING,
                        subtypes: [webcadDefines.ExtrasSubType.CEILING]
                    }
                ]
            },
            // - 壁 --
            {
                dialogType: webcadDefines.PartType.wall,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.KABE,
                        subtypes: [
                            webcadDefines.ExtrasSubType.OUTWALL,
                            webcadDefines.ExtrasSubType.INNERWALL,
                            webcadDefines.ExtrasSubType.TUMA
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.KOSI,
                        subtypes: [
                            webcadDefines.ExtrasSubType.OUTWALL,
                            webcadDefines.ExtrasSubType.INNERWALL,
                            webcadDefines.ExtrasSubType.BALCONY,
                            webcadDefines.ExtrasSubType.KASAGI
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.HABAKI,
                        subtypes: [webcadDefines.ExtrasSubType.HABAKI]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.KAMACHI,
                        subtypes: [webcadDefines.ExtrasSubType.KAMACHI]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.SECTION,
                        subtypes: [webcadDefines.ExtrasSubType.SECTION]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.BASE,
                        subtypes: [webcadDefines.ExtrasSubType.BASE]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.BASE,
                        subtypes: [webcadDefines.ExtrasSubType.MIZUKIRI]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.DEC,
                        subtypes: [webcadDefines.ExtrasSubType.MAKUITA, webcadDefines.ExtrasSubType.RAIL]
                    },
                ]
            },

            // - 床 --
            {
                dialogType: webcadDefines.PartType.floor,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.FLOOR,
                        subtypes: [
                            webcadDefines.ExtrasSubType.FLOOR,
                            webcadDefines.ExtrasSubType.ENTRANCE,
                            webcadDefines.ExtrasSubType.BALCONY
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.PORCH,
                        subtypes: [webcadDefines.ExtrasSubType.PORCH]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.STR,
                        subtypes: [webcadDefines.ExtrasSubType.STR]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.LIGHT,
                        subtypes: [webcadDefines.ExtrasSubType.LIGHT]
                    }
                ]
            },
            // - サッシ --
            {
                dialogType: webcadDefines.PartType.sash,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.DOOR,
                        subtypes: [webcadDefines.ExtrasSubType.SASH]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.WIN,
                        subtypes: [webcadDefines.ExtrasSubType.SASH]
                    }
                ]
            },
            // - 建具 --
            {
                dialogType: webcadDefines.PartType.fitting,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.DOOR,
                        subtypes: [
                            webcadDefines.ExtrasSubType.ENTRANCE,
                            webcadDefines.ExtrasSubType.FITTING,
                            webcadDefines.ExtrasSubType.TOILET,
                            webcadDefines.ExtrasSubType.BATHROOM
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.SHUTTER,
                        subtypes: [webcadDefines.ExtrasSubType.SHUTTER]
                    }
                ]
            },
            // - 住宅設備 --
            {
                dialogType: webcadDefines.PartType.equipment,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.EQU,
                        subtypes: [webcadDefines.ExtrasSubType.EQU]
                    }
                ]
            },
            // - 家具 --
            {
                dialogType: webcadDefines.PartType.furniture,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.FUR,
                        subtypes: [webcadDefines.ExtrasSubType.FUR]
                    }
                ]
            },
            // - エクステリア --
            {
                dialogType: webcadDefines.PartType.exterior,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.EXT,
                        subtypes: [webcadDefines.ExtrasSubType.EXT]
                    },
                ]
            },
            // - 敷地 --
            {
                dialogType: webcadDefines.PartType.site,
                extrasTypes: [
                    {
                        extrasType: webcadDefines.ExtrasType.GROUND,
                        subtypes: [webcadDefines.ExtrasSubType.GROUND]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.ROAD,
                        subtypes: [
                            webcadDefines.ExtrasSubType.ROAD,
                            webcadDefines.ExtrasSubType.CENTERLINE
                        ]
                    },
                    {
                        extrasType: webcadDefines.ExtrasType.LIMIT,
                        subtypes: [webcadDefines.ExtrasSubType.LIMIT]
                    }
                ]
            }
        ]
        return result;
    }


    /* setHouseObjectVisible
     * house object の表示/非表示.
     */
    public setHouseObjectVisible() {
        const objects = this.getAllFloorObjects();
        const itemList = View3dGlobal.shared.partType;
        for (let i = 0; i < objects.length; i++) {
            const obj = objects[i];

            const layer = obj.userData.layer;
            if (this.floorsObjects.floor < webcadDefines.Limits.roofNo) {
                if (layer && layer.indexOf('R') > -1 && Number(layer.substring(1, 2)) >= this.floorsObjects.floor) continue;
                if (layer && (obj as any).name.indexOf('ceiling') > -1 && Number(layer.substring(1, 2)) >= this.floorsObjects.floor) continue;
                if (layer && Number(layer.substring(1, 2)) > this.floorsObjects.floor) continue;
            }

            // 表示・非表示切り替え
            const visible = (itemType: string) => {
                if (itemType == null) return;
                obj.visible = itemList.find(value => value.id == itemType).visible;
            }

            const typeTable = this.typeTable();
            const userData = obj.userData;
            let typeObj: visibleDialogType;
            // 本体データ（表示・非表示）
            if (userData.type && userData.sub_type) {
                typeObj = typeTable.find((types: visibleDialogType) => {
                    return types.extrasTypes.find((type: visibleItemType) => {
                        if (type.extrasType != userData.type) return false;
                        return type.subtypes.some((subtype: string) => subtype == userData.sub_type);
                    });
                });
                if (typeObj == null) continue;
                visible(typeObj.dialogType);
            }
            // 配置部品（表示・非表示）
            if (!userData.modelInfo) continue;
            if(userData.modelInfo._typeExtras && userData.modelInfo._subType) {
                typeObj = typeTable.find((types: visibleDialogType) => {
                    return types.extrasTypes.find((type: visibleItemType) => {
                        if (type.extrasType != userData.modelInfo._typeExtras) return false;
                        return type.subtypes.some((subtype: string) => subtype == userData.modelInfo._subType);
                    });
                });
                if (typeObj == null) continue;
                visible(typeObj.dialogType);
            }
        }
    }

    /* calcFloorViewHeight
     * floorViewの高さを計算.
     */
    public calcFloorViewHeight(floor: number, pos: THREE.Vector3, measureLined: boolean): number {
        const fhgt: number = this._floorHeights[Math.max(webcadDefines.Limits.minFloor, Math.min(webcadDefines.Limits.maxFloor, floor)) - 1]; // use floor here.
        const csiz = CanvasInfo.get().size;
        let xsiz = this._xSize;
        let ysiz = this._ySize;
        const margin = (0.5 * 2) + (measureLined ? (2 * 2 + 1) : (1 * 2 + 1));
        xsiz += margin; ysiz += margin;

        const isH: boolean = (csiz.x / xsiz) < (csiz.y / ysiz);
        const aspect = csiz.x / csiz.y;
        const len: number = isH ? xsiz / aspect : ysiz;
        const ocam: THREE.PerspectiveCamera = <THREE.PerspectiveCamera>(ThreeView.get().cameras.getCamera(defines.CameraDefs.orbitCamNo).me);
        const vFOV: number = ocam.fov * Math.PI / 180;
        const dist: number = len / (2.0 * Math.tan(vFOV / 2));
        return dist + fhgt;
    }

    /*
     * ワイヤフレーム表示.
     */
    public wireframable(mesh: THREE.Mesh): boolean {
        return true;
    }

    public get wireframe(): boolean {
        return this.scene.wireframe;
    }

    private getObjectId(obj: any): string {
        if (obj.userData.attribute && obj.userData.attribute.objectId) return obj.userData.attribute.objectId;
        if (obj.parent) return this.getObjectId(obj.parent);
        return '';
    }

    private getWfColor(obj: any, isBody: boolean): number {
        let color = 0x20ff20;
        let name = '';
        if (isBody) {
            // console.log("BODY:" + obj.parent.name);
            if (obj.parent.name) name = obj.parent.name;
            if (name.match(/(roof-node|hana-node|mune-node)$/)) color = 0xff3333;
            else if (name.match(/porch-node$/) || name.match(/^out00-(road|site)/)) color = 0x4040ff;
            else if (name.match(/^(win|door|ent|sash|curtain|shutter)/)) color = 0xff3333;
        } else {
            name = this.getObjectId(obj);
            if (name.match(/^fur/)) color = 0xa349a4;
            else if (name.match(/^(equ|lig)/)) color = 0xff8040;
            else if (name.match(/^ext/)) color = 0x0080ff;
        }
        return color;
    }

    public set wireframe(value: boolean) {
        if (value) {
            if (!this.scene.wireframe) {
                for (let i = 0; i < this.houseObjects.children.length; i++) {
                    if (!this.houseObjects.children[i].visible) continue;
                    this.houseObjects.children[i].traverse((child) => {
                        if ((<THREE.Mesh>child).isMesh) {
                            const mesh: THREE.Mesh = <THREE.Mesh>(child);
                            if (this.wireframable(mesh)) {
                                const color: number = this.getWfColor(child, true);// 0x00ff00;

                                const wireframe = new THREE.WireframeGeometry(mesh.geometry);
                                const line = new THREE.LineSegments(wireframe, new THREE.LineBasicMaterial({ color }));
                                (line.material as THREE.Material).opacity = 0.1;
                                const position = new THREE.Vector3();
                                const quaternion = new THREE.Quaternion();
                                const scale = new THREE.Vector3();

                                mesh.matrixWorld.decompose(position, quaternion, scale);
                                line.position.add(position);
                                line.quaternion.copy(quaternion);
                                line.scale.copy(scale);
                                (line.material as THREE.Material).transparent = true;
                                this.scene.addWireframe(line);
                            }
                        }
                    });
                }
                for (let i = 0; i < this.furnitureObjects.length; i++) {
                    if (!this.furnitureObjects[i].visible) continue;
                    this.furnitureObjects[i].traverse((child) => {
                        if ((<THREE.Mesh>child).isMesh) {
                            const mesh: THREE.Mesh = <THREE.Mesh>(child);
                            const color: number = this.getWfColor(child, false);// 0x00cc00;
                            const wireframe = new THREE.WireframeGeometry(mesh.geometry);

                            const line = new THREE.LineSegments(wireframe, new THREE.LineBasicMaterial({ color }));
                            (line.material as THREE.Material).opacity = 0.1;
                            (line.material as THREE.Material).transparent = true;

                            const position = new THREE.Vector3();
                            const quaternion = new THREE.Quaternion();
                            const scale = new THREE.Vector3();

                            mesh.matrixWorld.decompose(position, quaternion, scale);
                            line.position.add(position);
                            line.quaternion.copy(quaternion);
                            line.scale.copy(scale);
                            this.scene.addWireframe(line);
                        }
                    });
                    this.furnitureObjects[i].visible = false;
                }
                this.houseObjects.visible = false;
            }
        } else {
            this.scene.wireframe = false;
            this.updateFloorVis();
            this.houseObjects.visible = true;
            View3dGlobal.shared.setItemDisplay();
        }
        this.eventDispatcher.dispatchEvent(new events.Event(webcadDefines.WebCadEventNames.wireframeModeChanged, this.scene.wireframe));
    }
}
